import { Inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { stringify } from 'qs';
import { OMNI_CONFIG, OmniConfig } from './gateway.config';
import { Observable, filter, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UrlFactory {
  _config: OmniConfig;
  constructor(@Inject(OMNI_CONFIG) private _config$: Observable<OmniConfig>) {
    this._config$
      .pipe(
        filter((conf: OmniConfig) => !!conf),
        take(1),
      )
      .subscribe((conf: OmniConfig) => (this._config = conf));
  }

  createIdentityUrl<TParams>(path: string, params?: Params<TParams>): string {
    return this.factory(this._config.omni.identityUrl, path, params);
  }
  createAwsIdentityUrl<TParams>(
    path: string,
    params?: Params<TParams>,
  ): string {
    return this.factory(this._config.omni.awsIdentityUrl, path, params);
  }
  createBotUrl<TParams>(path: string, params?: Params<TParams>): string {
    return this.factory(this._config.omni.botUrl, path, params);
  }
  createAssistantUrl<TParams>(path: string, params?: Params<TParams>): string {
    return this.factory(
      this._config.omni.publicUrl as string,
      `assistant/v2/${path}`,
      params,
    );
  }
  createLiveUrl<TParams>(path: string, params?: Params<TParams>): string {
    return this.factory(this._config.omni.liveUrl, path, params);
  }
  createAwsLiveUrl<TParams>(path: string, params?: Params<TParams>): string {
    return this.factory(this._config.omni.awsLiveUrl, path, params);
  }
  createAssetsUrl<TParams>(path: string, params?: Params<TParams>): string {
    // OmniConfig types `api.gateways` as required, but not every tenant
    // configures it at runtime, so keep returning '' when it is missing.
    const { api } = this._config as { api?: Partial<OmniConfig['api']> };
    if (api?.gateways) {
      return this.factory(api.gateways.cdn, path, params);
    }
    return '';
  }

  createPublicUrl<TParams>(path: string, params?: Params<TParams>): string {
    return this.factory(this._config.omni.publicUrl as string, path, params);
  }

  private factory<TParams>(
    base: string,
    path: string,
    params?: Params<TParams>,
  ): string {
    const pathWithParams =
      Location.stripTrailingSlash(path) + toQueryString(params);
    return Location.joinWithSlash(base, pathWithParams);
  }
}

export type ParamType = string | string[] | number | boolean | undefined;

export type Params<TParams> = { [P in keyof TParams]: ParamType };

export function toQueryString<TParams>(
  params?: Params<TParams>,
  encode = true,
): string {
  if (!params) {
    return '';
  }

  return stringify(params, {
    encode,
    indices: false,
    skipNulls: true,
    addQueryPrefix: true,
  });
}
